import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRouter } from './AppRouter';
import ToastProvider from './components/ui/ToastProvider';
// Create theme with the new professional banking color scheme
const theme = createTheme({
  palette: {
    primary: {
      main: '#0052CC',
      light: '#F0F3F7',
      dark: '#003DA8',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#F3F5F7',
      light: '#FFFFFF',
      dark: '#E5E7EB',
      contrastText: '#0052CC'
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280'
    },
    error: {
      main: '#AE2A19',
      light: '#FEF2F2'
    },
    success: {
      main: '#0B7923',
      light: '#ECFDF5'
    },
    warning: {
      main: '#B38600',
      light: '#FFFBEB'
    },
    info: {
      main: '#0052CC',
      light: '#EFF6FF'
    },
    divider: '#E5E7EB'
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1F2937'
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#1F2937'
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: '#1F2937'
    },
    body1: {
      fontSize: '0.875rem',
      color: '#374151'
    },
    body2: {
      fontSize: '0.875rem',
      color: '#374151'
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1F2937'
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#1F2937'
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          transition: 'none',
          scrollBehavior: 'smooth'
        },
        a: {
          color: 'inherit',
          textDecoration: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          transition: 'box-shadow 200ms ease',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        elevation1: {
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 6,
          backgroundColor: '#FFFFFF',
          transition: 'all 180ms ease',
          '&.Mui-focused': {
            boxShadow: 'none'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D1D5DB'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0052CC',
            borderWidth: 2
          }
        },
        notchedOutline: {
          borderColor: '#D1D5DB',
          borderWidth: 1
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#374151'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '10px 20px',
          boxShadow: 'none',
          transition: 'all 200ms ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
          },
          '&:focus-visible': {
            outline: '2px solid #0052CC',
            outlineOffset: 2
          }
        },
        contained: {
          backgroundColor: '#0052CC',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#003DA8'
          },
          '&:active': {
            backgroundColor: '#002D7A'
          },
          '&.Mui-disabled': {
            backgroundColor: '#D1D5DB',
            color: '#9CA3AF'
          }
        },
        outlined: {
          borderColor: '#D1D5DB',
          borderWidth: 1,
          backgroundColor: '#F3F5F7',
          color: '#0052CC',
          '&:hover': {
            backgroundColor: '#E5E7EB',
            borderColor: '#D1D5DB'
          }
        },
        text: {
          '&:hover': {
            backgroundColor: '#F0F3F7'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          fontWeight: 600,
          fontSize: '0.75rem',
          transition: 'none',
          '&:hover': {
            transform: 'none'
          }
        },
        filled: {
          backgroundColor: '#F3F5F7',
          color: '#374151',
          border: 'none',
          '&.Mui-selected': {
            backgroundColor: '#0052CC',
            color: '#FFFFFF'
          },
          '&:hover': {
            backgroundColor: '#E5E7EB'
          }
        },
        outlined: {
          borderWidth: 1,
          borderColor: '#E5E7EB',
          backgroundColor: 'transparent'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '12px 16px',
          borderColor: '#E5E7EB',
          color: '#374151'
        },
        head: {
          fontSize: '0.875rem',
          fontWeight: 700,
          backgroundColor: '#F3F5F7',
          color: '#0052CC',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'none',
          backgroundColor: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#F9FAFB',
            transform: 'none',
            boxShadow: 'none'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'none',
          '&:hover': {
            backgroundColor: '#F0F3F7',
            transform: 'none'
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'none',
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E5E7EB',
          backgroundColor: '#F8F9FA'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          borderRadius: 6,
          padding: '6px 12px',
          fontSize: '0.75rem',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
        },
        arrow: {
          color: '#1F2937'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',
          border: '1px solid #E5E7EB'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: 'none',
          '&.Mui-selected': {
            backgroundColor: '#F0F3F7',
            fontWeight: 600,
            color: '#0052CC',
            '&::before': {
              display: 'none'
            }
          },
          '&:hover': {
            backgroundColor: '#F9FAFB'
          }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: '#E5E7EB'
        },
        bar: {
          borderRadius: 4,
          backgroundColor: '#0052CC'
        }
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          strokeLinecap: 'round'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 46,
          height: 24,
          padding: 0
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(22px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': {
              backgroundColor: '#0052CC',
              opacity: 1
            }
          }
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
        },
        track: {
          borderRadius: 50,
          opacity: 1,
          backgroundColor: '#D1D5DB'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0px 20px 25px rgba(0,0,0,0.15)',
          border: '1px solid #E5E7EB'
        }
      }
    }
  }
});
// Scroll to top component to reset scroll position on route changes
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
export function App() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  // Check for existing session on app load
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    if (authStatus === 'true') {
      setIsLoggedIn(true);
    }
  }, []);
  const handleLogin = (username: string, password: string): boolean => {
    if (username === 'admin' && password === 'test@1234') {
      setIsLoggedIn(true);
      localStorage.setItem('isAuthenticated', 'true');
      return true;
    }
    return false;
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    // Remove authentication status from localStorage
    localStorage.removeItem('isAuthenticated');
  };
  return <ThemeProvider theme={theme} data-id="element-0">
      <CssBaseline data-id="element-1" />
      <ToastProvider data-id="element-2">
        <BrowserRouter data-id="element-3">
          <ScrollToTop data-id="element-4" />
          <AppRouter isLoggedIn={isLoggedIn} onLogout={handleLogout} onLogin={handleLogin} data-id="element-5" />
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>;
}